import {
  Button,
  CompanyCredit,
  Select,
  TextField,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from "@stagvista/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowDownAZ, ArrowDownZA, CalendarArrowDown, CalendarArrowUp } from "lucide-react";
import * as React from "react";

import { useCloudSession } from "../hooks/use-cloud-session.js";
import { CloudApiError } from "../lib/cloud-api-client.js";
import { formatCreated, formatCreatedLong } from "../lib/format-created.js";
import { EVENT_SORTS, isEventSort, sortEvents, type EventSort } from "../lib/sort-events.js";

export const Route = createFileRoute("/")({
  component: NewEventPage,
});

/** `apps/cloud-api`'s `Event` response shape (`POST /v1/events`) — only the fields this page actually reads. */
interface EventDto {
  readonly id: string;
  readonly name: string;
  readonly createdAt?: string | null;
}

const SORT_STORAGE_KEY = "stagvista.admin.eventSort";
const SORT_ICONS: Record<EventSort, React.ComponentType<{ className?: string }>> = {
  newest: CalendarArrowDown,
  oldest: CalendarArrowUp,
  "name-asc": ArrowDownAZ,
  "name-desc": ArrowDownZA,
};

/** Remembers the chosen order between visits; storage can be blocked, so it is never relied on. */
function readSavedSort(): EventSort {
  try {
    const saved = window.localStorage.getItem(SORT_STORAGE_KEY);
    return isEventSort(saved) ? saved : "newest";
  } catch {
    return "newest";
  }
}

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** `docs/implementation/23-Admin-Event-Console.md` §9's own simplification note: a real IANA zone list, but `startsAt` itself is read from a plain `datetime-local` input interpreted in the *browser's* local time, not re-projected into whichever zone is picked here — good enough for "pick roughly when this happens," not a claim of full timezone-aware scheduling. */
const TIMEZONE_OPTIONS = (
  typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : [BROWSER_TIMEZONE]
).map((zone) => ({ value: zone, label: zone }));

function NewEventPage(): React.ReactElement {
  const { session, signOut } = useCloudSession();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!session) void navigate({ to: "/sign-in" });
  }, [session, navigate]);

  if (!session) return <></>;
  return <NewEventForm onSignOut={signOut} />;
}

function NewEventForm({ onSignOut }: { onSignOut: () => void }): React.ReactElement {
  const { client } = useCloudSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [name, setName] = React.useState("");
  const [startsAt, setStartsAt] = React.useState("");
  const [timezone, setTimezone] = React.useState(BROWSER_TIMEZONE);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const isoStartsAt = new Date(startsAt).toISOString();
      const result = await client.request<EventDto>("POST", "/v1/events", {
        name,
        startsAt: isoStartsAt,
        timezone,
      });
      toast({
        tone: "success",
        title: "Event created",
        description: `"${result.name}" is ready — add its services next.`,
      });
      await navigate({ to: "/events/$eventId", params: { eventId: result.id } });
    } catch (err) {
      const message =
        err instanceof CloudApiError
          ? (err.problem.detail ?? err.problem.title)
          : "Could not create the event.";
      setError(message);
      toast({ tone: "error", title: "Couldn't create event", description: message });
      setSubmitting(false);
    }
  }

  function handleSignOut(): void {
    onSignOut();
    void navigate({ to: "/sign-in" });
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="grid min-h-screen gap-4 p-4 lg:grid-cols-2">
        <ExistingEventsPanel />

        <div className="order-1 flex items-center justify-center px-2 py-6 sm:px-8">
          <div className="w-full max-w-sm text-slate-900">
            <img
              src="/images/logo-light.png"
              alt="StagVista"
              className="mx-auto mb-8 h-12 w-auto"
            />
            <div className="mb-6 flex items-center justify-between">
              <h1 className="text-2xl font-semibold">New event</h1>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                Sign out
              </Button>
            </div>

            <form className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
              <TextField
                label="Event name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                hint="What the operator will see when it appears on their next sync."
              />
              <TextField
                label="Starts at"
                type="datetime-local"
                required
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                error={error ?? undefined}
              />
              <Select
                label="Timezone"
                options={TIMEZONE_OPTIONS}
                value={timezone}
                onChange={setTimezone}
              />
              <Button type="submit" loading={submitting} className="mt-2">
                Create event
              </Button>
            </form>

            <CompanyCredit className="mt-8 text-center text-slate-400" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** The left-hand panel: the organization's existing events, one click from their services page. */
function ExistingEventsPanel(): React.ReactElement {
  const { client } = useCloudSession();
  const [events, setEvents] = React.useState<EventDto[] | null>(null);
  const [sort, setSort] = React.useState<EventSort>(readSavedSort);

  React.useEffect(() => {
    client
      .request<EventDto[]>("GET", "/v1/events")
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [client]);

  const sorted = React.useMemo(() => (events ? sortEvents(events, sort) : null), [events, sort]);

  function chooseSort(next: EventSort): void {
    setSort(next);
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, next);
    } catch {
      // Storage blocked (private window): the choice just is not remembered.
    }
  }

  return (
    <div className="relative order-2 flex flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 p-6 text-white sm:p-8">
      <div className="pointer-events-none absolute -bottom-16 -left-16 size-56 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-white/10" />

      <div className="relative flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Your events</h2>
          <p className="mt-1 text-body text-white/80">
            Pick one to manage its services, or create a new one.
          </p>
        </div>

        {events && events.length > 1 ? (
          <div
            role="radiogroup"
            aria-label="Sort events"
            className="flex shrink-0 gap-0.5 rounded-full border border-white/30 bg-white/10 p-0.5 pr-0.5"
          >
            {EVENT_SORTS.map((option) => {
              const Icon = SORT_ICONS[option.value];
              const active = option.value === sort;
              return (
                <Tooltip key={option.value}>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        role="radio"
                        aria-checked={active}
                        aria-label={option.label}
                        onClick={() => chooseSort(option.value)}
                        className={
                          "grid size-8 place-items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white " +
                          (active ? "bg-white text-blue-700" : "text-white hover:bg-white/20")
                        }
                      >
                        <Icon className="size-4" aria-hidden="true" />
                      </button>
                    }
                  />
                  <TooltipContent side="bottom">{option.hint}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="relative mt-4" />
      <div className="relative min-h-0 flex-1">
        {sorted === null ? (
          <div className="space-y-2" aria-hidden="true">
            {[0, 1, 2].map((row) => (
              <div key={row} className="h-11 animate-pulse rounded-lg bg-white/15" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <p className="rounded-lg border border-white/20 bg-white/10 px-4 py-6 text-center text-body text-white/80">
            No events yet — create your first one.
          </p>
        ) : (
          <ul className="flex flex-col gap-2 lg:absolute lg:inset-0 lg:overflow-y-auto lg:pr-2 [scrollbar-color:rgba(255,255,255,0.4)_transparent] [scrollbar-width:thin]">
            {sorted.map((event) => (
              <li key={event.id}>
                <Link
                  to="/events/$eventId"
                  params={{ eventId: event.id }}
                  className="flex items-center justify-between gap-3 rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-body-strong text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <span className="min-w-0 truncate">{event.name}</span>
                  {formatCreated(event.createdAt) ? (
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <time
                            dateTime={event.createdAt ?? undefined}
                            className="shrink-0 whitespace-nowrap text-caption font-normal text-white/70"
                          >
                            {formatCreated(event.createdAt)}
                          </time>
                        }
                      />
                      <TooltipContent side="bottom">
                        Created {formatCreatedLong(event.createdAt)}
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
